import React from 'react';
import axios from 'axios';
import { AlertCircle, Bot, Check, ChevronDown, ChevronUp, Clock, Edit3, GitBranch, GitMerge, Globe, Loader, MapPin, Megaphone, Mic, MicOff, Play, Pause, RotateCcw, Scissors, Shield, Sparkles, Trash2, User, UserCheck, Volume2, X } from 'lucide-react';
import ClearableField from './ClearableField';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import Tabs from './ui/Tabs';
import HistoricalDatePicker from './HistoricalDatePicker';
import LeafletMap from './LeafletMap';
import FamilyTree from './FamilyTree';
import Timeline from './Timeline';
import { AppLogoMark } from '../LogoEditor';
import { PLACE_TYPE_COLOURS, PLACE_TYPE_ICONS, PLACE_TYPE_LABELS } from '../constants/map';
import { Z } from '../constants/tokens';
import { getLanguageMeta } from '../constants/languages';
import { PlayButton } from './AudioClipPlayer';
import { useToast } from '../toastContext';
import { useConfirm } from '../confirmContext';
import { usePrompt } from '../promptContext';

const SubjectEditForm = React.forwardRef(function SubjectEditForm({ root, initial, theme, backendUrl, isDarkMode, currentUser = null, onSaved, onSeedStart, onSeedDone, onSeedStateChange = null, pushToast = null, logActivity = null, videoId, activeSubdir = '', part, onSeekVideo, onPlayClip = null, onOpenClips = null, externalTab = null, onTabChange = null, hideInternalTabs = false, fullscreen = false, cloudTtsAvailable = false, onOpenMergeReview = null }, ref) {
  // The pushToast prop defaults to null, so fall back to the app-level toast
  // context — an error must never be swallowed just because a caller omitted it.
  const { pushToast: ctxPushToast } = useToast();
  const toast = pushToast || ctxPushToast;
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [familyCount, setFamilyCount] = React.useState(null);  // null = not yet loaded
  const [form, setForm] = React.useState({
    display_name:       initial.display_name       || '',
    name_phonetic:      initial.name_phonetic      || '',
    name_ipa:           initial.name_ipa           || '',
    name_hebrew:        initial.name_hebrew        || '',
    born:               initial.born               || '',
    birthplace:         initial.birthplace         || '',
    death_year:         initial.death_year         || '',
    death_place:        initial.death_place        || '',
    auschwitz_number:   initial.auschwitz_number   || '',
    interview_date:     initial.interview_date     || '',
    interviewer:        initial.interviewer        || '',
    interview_place:    initial.interview_place    || '',
    languages:          initial.languages          || '',
    religion:           initial.religion           || '',
    prewar_address:     initial.prewar_address     || '',
    notes:              initial.notes              || '',
    confidence:         initial.confidence         || 'ai_draft',
    // Vital statistics
    nationality:        initial.nationality        || '',
    mother_tongue:      initial.mother_tongue      || '',
    father_name:        initial.father_name        || '',
    mother_name:        initial.mother_name        || '',
    mother_maiden_name: initial.mother_maiden_name || '',
    num_siblings:       initial.num_siblings       || '',
  });
  const [places, setPlaces]     = React.useState([]);
  const [placeTypeFilter, setPlaceTypeFilter] = React.useState('all');
  const [focusedPlaceId, setFocusedPlaceId] = React.useState(null);
  const [placeMergeSelection, setPlaceMergeSelection] = React.useState(new Set());
  const [placeMerging, setPlaceMerging] = React.useState(false);
  const [placeAiReviewing, setPlaceAiReviewing] = React.useState(false);
  const [placeEnriching, setPlaceEnriching] = React.useState(false);
  const [editingPlace, setEditingPlace] = React.useState(null); // place object being edited
  const [subjectCosts, setSubjectCosts] = React.useState(null);
  const [showCostBreakdown, setShowCostBreakdown] = React.useState(false);
  const [tab, setTab]           = React.useState('bio');
  const [saving, setSaving]     = React.useState(false);
  const [seeding, setSeeding]   = React.useState(false);
  const [seedPreview, setSeedPreview] = React.useState(null); // { preview, summary }
  const [seedApplying, setSeedApplying] = React.useState(false);
  const [err, setErr]           = React.useState(null);
  const [playingPronunciation, setPlayingPronunciation] = React.useState(false);
  const [pronunciationAudio, setPronunciationAudio] = React.useState(null);
  const [pronunciationClip, setPronunciationClip] = React.useState(null);

  // ── Pronunciation Tool panel (state only — effects after interviewLang) ──
  const [showPronunciationTool, setShowPronunciationTool] = React.useState(false);
  const [pronToolTab, setPronToolTab] = React.useState('tts');
  const [ttsVoices, setTtsVoices] = React.useState([]);
  const [ttsPlaying, setTtsPlaying] = React.useState(null);
  const [ttsSelected, setTtsSelected] = React.useState(null);
  const [geminiVoice, setGeminiVoice] = React.useState('Kore');
  const [geminiGenerating, setGeminiGenerating] = React.useState(false);
  const [geminiAudioUrl, setGeminiAudioUrl] = React.useState(null);
  const [suggestingIpa, setSuggestingIpa] = React.useState(false);
  const [recording, setRecording] = React.useState(false);
  const [recordedBlob, setRecordedBlob] = React.useState(null);
  const [recordedUrl, setRecordedUrl] = React.useState(null);
  const [playingRecorded, setPlayingRecorded] = React.useState(false);
  const [uploadingRecording, setUploadingRecording] = React.useState(false);
  const [hasUserRecording, setHasUserRecording] = React.useState(false);
  const mediaRecorderRef = React.useRef(null);
  const recordedChunksRef = React.useRef([]);
  const ttsUtteranceRef = React.useRef(null);

  // ── Interview language — lives in interview_meta, fetched/saved separately ──
  const [interviewLang,       setInterviewLang]       = React.useState('');
  const [interviewLangSaving, setInterviewLangSaving] = React.useState(false);
  const [knownLangs,          setKnownLangs]          = React.useState([]);
  // Free-text escape hatch for the Interview Language picker: true while the
  // user is typing a language that is not in knownLangs yet.
  const [interviewLangOther,  setInterviewLangOther]  = React.useState(false);
  const authConfig = React.useMemo(() => {
    const tok = sessionStorage.getItem('gwv_token');
    return tok ? { headers: { Authorization: `Bearer ${tok}` } } : {};
  }, [root]);

  React.useEffect(() => {
    if (!root) return;
    axios.get(`${backendUrl}/pipeline/language?root=${encodeURIComponent(root)}`, authConfig)
      .then(r => { if (r.data.interview_language) setInterviewLang(r.data.interview_language); })
      .catch(() => {});
    axios.get(`${backendUrl}/pipeline/languages`, authConfig)
      .then(r => setKnownLangs([...new Set([...(r.data.used || []), ...(r.data.all || [])])]))
      .catch(() => {});
  }, [root, backendUrl, authConfig]);
  const [lastSeededAt, setLastSeededAt] = React.useState(initial.last_seeded_at || null);
  const interviewLangMeta = React.useMemo(() => getLanguageMeta(interviewLang), [interviewLang]);

  // ── Pronunciation Tool effects (need interviewLang + authConfig) ──
  React.useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis?.getVoices() || [];
      const english = voices.filter(v => v.lang.startsWith('en'));
      const other = interviewLang ? voices.filter(v => v.lang.toLowerCase().startsWith(interviewLang.toLowerCase())) : [];
      const unique = new Map();
      [...other, ...english].forEach(v => { if (!unique.has(v.name)) unique.set(v.name, v); });
      setTtsVoices([...unique.values()].slice(0, 12));
    };
    loadVoices();
    window.speechSynthesis?.addEventListener?.('voiceschanged', loadVoices);
    return () => window.speechSynthesis?.removeEventListener?.('voiceschanged', loadVoices);
  }, [interviewLang]);

  React.useEffect(() => {
    if (!root) return;
    axios.get(`${backendUrl}/subject/pronunciation-recording?root=${encodeURIComponent(root)}&check=1`, authConfig)
      .then(r => setHasUserRecording(!!r.data?.exists))
      .catch(() => {});
  }, [root, backendUrl, authConfig]);

  const playTtsVoice = React.useCallback((voice, idx) => {
    window.speechSynthesis?.cancel();
    const text = (form.name_phonetic || form.display_name || '').trim();
    if (!text) return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = voice;
    utterance.rate = 0.85;
    utterance.onstart = () => setTtsPlaying(idx);
    utterance.onend = () => setTtsPlaying(null);
    utterance.onerror = () => setTtsPlaying(null);
    ttsUtteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [form.name_phonetic, form.display_name]);

  const [fieldPlaying, setFieldPlaying] = React.useState(null); // 'hebrew' | 'phonetic' | 'ipa' | null
  const playFieldAudio = React.useCallback((text, fieldId) => {
    if (!text?.trim()) return;
    setFieldPlaying(fieldId);

    // IPA → Cloud TTS with SSML phoneme tags (only engine that interprets IPA)
    // Phonetic → Gemini TTS with the display name (phonetic guide is for humans)
    // Hebrew → Cloud TTS with a native he-IL voice — voices Hebrew reliably and
    //          does not depend on the Gemini key (the old Gemini path failed
    //          silently whenever that key could not be loaded).
    let endpoint, payload;
    if (fieldId === 'ipa') {
      endpoint = `${backendUrl}/subject/pronunciation-cloud-tts`;
      payload = { name: form.display_name, ipa: text.trim(), voice: 'en-US-Neural2-D', lang: 'en-US', project_root: root };
    } else if (fieldId === 'phonetic') {
      endpoint = `${backendUrl}/subject/pronunciation-preview`;
      payload = { name: form.display_name, phonetic: '', ipa: '', voice: 'Kore', project_root: root };
    } else {
      endpoint = `${backendUrl}/subject/pronunciation-cloud-tts`;
      payload = { hebrew: text.trim(), lang: 'he-IL', voice: 'he-IL-Wavenet-A', project_root: root };
    }

    axios.post(endpoint, payload, { ...authConfig, responseType: 'blob' })
      .then(({ data }) => {
        const url = URL.createObjectURL(data);
        const a = new Audio(url);
        a.onended = () => { setFieldPlaying(null); URL.revokeObjectURL(url); };
        a.onerror = () => { setFieldPlaying(null); URL.revokeObjectURL(url); };
        a.play();
      })
      .catch(() => setFieldPlaying(null));
  }, [backendUrl, authConfig, form.display_name, root]);

  const stopTts = React.useCallback(() => {
    window.speechSynthesis?.cancel();
    setTtsPlaying(null);
  }, []);

  const startRecording = React.useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4' });
      recordedChunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(recordedChunksRef.current, { type: mr.mimeType });
        setRecordedBlob(blob);
        const url = URL.createObjectURL(blob);
        if (recordedUrl) try { URL.revokeObjectURL(recordedUrl); } catch {}
        setRecordedUrl(url);
        // Auto-save recording immediately
        if (root && blob.size > 0) {
          const fd = new FormData();
          fd.append('file', blob, 'pronunciation.webm');
          axios.post(`${backendUrl}/subject/pronunciation-recording?root=${encodeURIComponent(root)}`, fd, {
            ...authConfig, headers: { ...authConfig.headers, 'Content-Type': 'multipart/form-data' },
          }).then(() => { setHasUserRecording(true); if (pushToast) pushToast('pron-saved', 'success', 'Recording saved'); })
            // A failed auto-save used to be swallowed entirely: no toast, no
            // 'saved' confirmation either, so the user was left staring at the
            // preview with no idea the upload had failed. Point them at the
            // Save button, which re-posts the same blob.
            .catch(e => toast('pron-saved', 'error', 'Could not save the recording automatically — press Save to retry. ' + (e?.response?.data?.detail || '')));
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setRecordedBlob(null);
    } catch (err) {
      toast('pron-mic', 'error', 'Microphone access denied. Please allow microphone access in your browser settings.');
    }
  }, [recordedUrl]);

  const stopRecording = React.useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  }, []);

  const uploadRecording = React.useCallback(async () => {
    if (!recordedBlob || !root) return;
    setUploadingRecording(true);
    const formData = new FormData();
    formData.append('file', recordedBlob, 'pronunciation.webm');
    try {
      await axios.post(`${backendUrl}/subject/pronunciation-recording?root=${encodeURIComponent(root)}`, formData, {
        ...authConfig,
        headers: { ...authConfig.headers, 'Content-Type': 'multipart/form-data' },
      });
      setHasUserRecording(true);
      setRecordedBlob(null);
      if (recordedUrl) try { URL.revokeObjectURL(recordedUrl); } catch {}
      setRecordedUrl(null);
      if (pushToast) pushToast('pron-saved', 'success', 'Pronunciation recording saved');
    } catch (err) {
      toast('pron-saved', 'error', err?.response?.data?.detail || 'Failed to save recording');
    } finally {
      setUploadingRecording(false);
    }
  }, [recordedBlob, root, backendUrl, authConfig, recordedUrl, pushToast]);

  React.useEffect(() => {
    setForm({
      display_name:       initial.display_name       || '',
      born:               initial.born               || '',
      birthplace:         initial.birthplace         || '',
      death_year:         initial.death_year         || '',
      death_place:        initial.death_place        || '',
      auschwitz_number:   initial.auschwitz_number   || '',
      interview_date:     initial.interview_date     || '',
      interviewer:        initial.interviewer        || '',
      interview_place:    initial.interview_place    || '',
      languages:          initial.languages          || '',
      religion:           initial.religion           || '',
      prewar_address:     initial.prewar_address     || '',
      notes:              initial.notes              || '',
      confidence:         initial.confidence         || 'ai_draft',
      nationality:        initial.nationality        || '',
      mother_tongue:      initial.mother_tongue      || '',
      father_name:        initial.father_name        || '',
      mother_name:        initial.mother_name        || '',
      mother_maiden_name: initial.mother_maiden_name || '',
      num_siblings:       initial.num_siblings       || '',
      name_phonetic:      initial.name_phonetic      || '',
      name_ipa:           initial.name_ipa           || '',
      name_hebrew:        initial.name_hebrew        || '',
    });
  }, [initial, root]);
  React.useEffect(() => {
    setLastSeededAt(initial.last_seeded_at || null);
  }, [initial.last_seeded_at, root]);

  // Format a UTC ISO timestamp → "HH:MM:SS D/M/YYYY" in local time
  const fmtSeeded = iso => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d)) return null;
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}  ${d.getDate()}/${d.getMonth()+1}/${d.getFullYear()}`;
  };

  // Load places on mount
  React.useEffect(() => {
    if (!root) return;
    axios.get(`${backendUrl}/subject/places?root=${encodeURIComponent(root)}`, authConfig)
      .then(r => setPlaces(r.data))
      .catch(() => {});
  }, [root, backendUrl, authConfig]);

  // Load AI costs for this subject (admin only)
  React.useEffect(() => {
    if (!root || currentUser?.role !== 'admin') return;
    axios.get(`${backendUrl}/subject/costs?root=${encodeURIComponent(root)}`, authConfig)
      .then(r => setSubjectCosts(r.data))
      .catch(() => setSubjectCosts({ total_cost: 0, total_runs: 0, success_count: 0, total_tokens: 0, recent_runs: [] }));
  }, [root, backendUrl, authConfig, currentUser]);
  const currentTab = externalTab ?? tab;
  React.useEffect(() => {
    if (!root || currentTab !== 'bio') return;
    axios.get(`${backendUrl}/clips/${encodeURIComponent(root)}`, authConfig)
      .then(r => {
        const clip = (r.data || []).find(c => c.use_for_pronunciation);
        setPronunciationClip(clip || null);
      })
      .catch(() => setPronunciationClip(null));
  }, [root, backendUrl, authConfig, currentTab]);
  // Auto-generate Hebrew script if empty and name exists
  const hebrewAutoGenRef = React.useRef(false);
  React.useEffect(() => {
    if (!root || !form.display_name || form.name_hebrew || hebrewAutoGenRef.current) return;
    // Only auto-generate once per subject
    hebrewAutoGenRef.current = true;
    axios.post(`${backendUrl}/subject/pronunciation-suggest-hebrew`, {
      name: form.display_name, project_root: root,
    }, authConfig)
      .then(r => {
        if (r.data?.hebrew) {
          setForm(f => ({ ...f, name_hebrew: r.data.hebrew }));
          // Auto-save the Hebrew field
          axios.put(`${backendUrl}/subject?root=${encodeURIComponent(root)}`, {
            ...form, name_hebrew: r.data.hebrew,
          }, authConfig).catch(() => {});
        }
      })
      .catch(() => {});
  }, [root, form.display_name, form.name_hebrew, backendUrl, authConfig]);

  React.useEffect(() => {
    hebrewAutoGenRef.current = false;
  }, [root]);

  React.useEffect(() => {
    setPlaceTypeFilter('all');
    setFocusedPlaceId(null);
  }, [root]);
  const filteredPlaces = React.useMemo(
    () => (placeTypeFilter === 'all' ? places : places.filter(pl => pl.place_type === placeTypeFilter)),
    [places, placeTypeFilter]
  );
  const placeNumberById = React.useMemo(() => {
    const numbered = filteredPlaces
      .filter(pl => pl.lat && pl.lon)
      .sort((a, b) => (a.year_from || 9999) - (b.year_from || 9999));
    return Object.fromEntries(numbered.map((pl, idx) => [pl.id, idx + 1]));
  }, [filteredPlaces]);
  const togglePlaceTypeFilter = React.useCallback((type) => {
    setFocusedPlaceId(null);
    setPlaceTypeFilter(prev => (prev === type ? 'all' : type));
  }, []);
  React.useEffect(() => {
    if (!focusedPlaceId) return;
    if (!filteredPlaces.some(pl => pl.id === focusedPlaceId)) {
      setFocusedPlaceId(null);
    }
  }, [filteredPlaces, focusedPlaceId]);
  const canDeletePlaces = currentUser?.role === 'admin';
  const canEdit = currentUser?.role === 'admin' || currentUser?.role === 'editor';
  const togglePlaceMerge = React.useCallback((id) => {
    setPlaceMergeSelection(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);
  const _toast = React.useCallback((id, type, msg, label) => {
    if (pushToast) pushToast(id, type, msg);
    if (logActivity) logActivity(id, 'places', label || msg, type, type === 'error' ? { message: msg } : {});
  }, [pushToast, logActivity]);
  const handlePlaceMerge = React.useCallback(async () => {
    if (placeMergeSelection.size < 2) return;
    const ids = [...placeMergeSelection];
    const selected = ids.map(id => places.find(p => p.id === id)).filter(Boolean);
    const placeLabel = (p) => `${p.canonical || p.place_name} (${p.place_type || '?'})`;
    const keepId = await prompt({
      title: 'Which record do you want to keep?',
      message: 'The others will be merged into it.',
      choices: selected.map(p => ({ value: p.id, label: placeLabel(p) })),
      confirmLabel: 'Merge',
    });
    if (keepId == null) return;
    const keepPlace = selected.find(p => p.id === keepId);
    const mergeIds = ids.filter(id => id !== keepId);
    if (!await confirm({
      message: `Merge ${mergeIds.length} place(s) into "${keepPlace.canonical || keepPlace.place_name}"?\nThe others will be deleted and their data combined.`,
      title: 'Merge places?',
      confirmLabel: 'Merge',
      destructive: true,
    })) return;
    setPlaceMerging(true);
    try {
      const res = await axios.post(`${backendUrl}/subject/places/merge?root=${encodeURIComponent(root)}`, { keep_id: keepId, merge_ids: mergeIds }, authConfig);
      _toast('places-merge', 'success', `Merged ${res.data.merged} place(s). Fields updated: ${(res.data.fields_updated || []).join(', ') || 'none'}`, 'Places Merge');
      setPlaceMergeSelection(new Set());
      const fresh = await axios.get(`${backendUrl}/subject/places?root=${encodeURIComponent(root)}`, authConfig);
      setPlaces(fresh.data);
    } catch (e) {
      _toast('places-merge', 'error', `Merge failed: ${e?.response?.data?.detail || e.message}`, 'Places Merge');
    } finally {
      setPlaceMerging(false);
    }
  }, [placeMergeSelection, places, backendUrl, root, authConfig, _toast, confirm, prompt]);
  const handlePlaceAiReview = React.useCallback(async () => {
    if (!await confirm({
      message: 'Send all places to AI for review?\nThis will enrich context with family data, detect duplicates, and correct data.',
      title: 'Run AI review?',
      confirmLabel: 'Run AI Review',
      destructive: false,
    })) return;
    setPlaceAiReviewing(true);
    const tid = 'places-ai-review';
    const label = 'AI Places Review';
    _toast(tid, 'running', 'AI reviewing places — cross-referencing with genogram…', label);
    try {
      const res = await axios.post(`${backendUrl}/subject/places/ai-review?root=${encodeURIComponent(root)}`, {}, authConfig);
      const dups = res.data.duplicates_found || [];
      let msg = `AI reviewed ${res.data.updated} place(s).`;
      if (dups.length > 0) msg += ` ${dups.length} duplicate(s) detected.`;
      _toast(tid, 'success', msg, label);
      const fresh = await axios.get(`${backendUrl}/subject/places?root=${encodeURIComponent(root)}`, authConfig);
      setPlaces(fresh.data);
    } catch (e) {
      _toast(tid, 'error', `AI review failed: ${e?.response?.data?.detail || e.message}`, label);
    } finally {
      setPlaceAiReviewing(false);
    }
  }, [backendUrl, root, authConfig, _toast, confirm]);
  const handlePlaceEnrichFromFamily = React.useCallback(async () => {
    setPlaceEnriching(true);
    const tid = 'places-enrich-family';
    const label = 'Enrich Places from Family';
    _toast(tid, 'running', 'Enriching places from genogram data…', label);
    try {
      const res = await axios.post(`${backendUrl}/subject/places/enrich-from-family?root=${encodeURIComponent(root)}`, {}, authConfig);
      _toast(tid, 'success', `Enriched ${res.data.updated} place(s) with family data.`, label);
      const fresh = await axios.get(`${backendUrl}/subject/places?root=${encodeURIComponent(root)}`, authConfig);
      setPlaces(fresh.data);
    } catch (e) {
      _toast(tid, 'error', `Enrichment failed: ${e?.response?.data?.detail || e.message}`, label);
    } finally {
      setPlaceEnriching(false);
    }
  }, [backendUrl, root, authConfig, _toast]);
  const canPlayPronunciation = Boolean(pronunciationClip);

  const field = (key, label, placeholder = '', type = 'input') => (
    <div style={{ marginBottom: '12px' }}>
      <label style={{ display: 'block', fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '3px' }}>{label}</label>
      {type === 'textarea'
        ? <ClearableField as="textarea" theme={theme} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} onClear={() => setForm(f => ({ ...f, [key]: '' }))}
            rows={3} placeholder={placeholder}
            style={{ width: '100%', padding: '7px 9px', background: theme.inputBg || theme.card, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '0.82rem', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
        : <ClearableField theme={theme} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} onClear={() => setForm(f => ({ ...f, [key]: '' }))}
            placeholder={placeholder}
            style={{ width: '100%', padding: '7px 9px', background: theme.inputBg || theme.card, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }} />
      }
    </div>
  );

  const playPronunciation = React.useCallback(() => {
    const text = (form.name_phonetic || form.display_name || '').trim();
    if (!text && !pronunciationClip) return;
    const revision = pronunciationClip
      ? `${pronunciationClip.id}-${pronunciationClip.updated_at || pronunciationClip.start_time || 0}`
      : `${form.name_phonetic || ''}::${form.display_name || ''}`;
    const url = `${backendUrl}/subject/pronunciation-audio?root=${encodeURIComponent(root)}&v=${encodeURIComponent(revision)}`;
    setPlayingPronunciation(true);
    axios.get(url, { ...authConfig, responseType: 'blob' })
      .then(({ data }) => {
        const objectUrl = URL.createObjectURL(data);
        const audio = pronunciationAudio || new Audio();
        if (pronunciationAudio?.src?.startsWith('blob:')) {
          try { URL.revokeObjectURL(pronunciationAudio.src); } catch {}
        }
        audio.src = objectUrl;
        audio.onplay = () => setPlayingPronunciation(true);
        audio.onended = () => {
          setPlayingPronunciation(false);
          try { URL.revokeObjectURL(objectUrl); } catch {}
        };
        audio.onerror = () => {
          setPlayingPronunciation(false);
          try { URL.revokeObjectURL(objectUrl); } catch {}
        };
        setPronunciationAudio(audio);
        audio.currentTime = 0;
        return audio.play();
      })
      .catch(() => setPlayingPronunciation(false));
  }, [authConfig, backendUrl, form.display_name, form.name_phonetic, pronunciationAudio, pronunciationClip, root]);

  React.useEffect(() => () => {
    try {
      if (pronunciationAudio) pronunciationAudio.pause();
    } catch {}
  }, [pronunciationAudio]);

  const _applySeedResult = (r) => {
    if (r.subject) {
      const s = r.subject;
      if (s.last_seeded_at) setLastSeededAt(s.last_seeded_at);
      if (r.interview_language) setInterviewLang(r.interview_language);
      setForm(f => ({
        ...f,
        display_name: s.display_name || f.display_name,
        born: s.born || f.born,
        birthplace: s.birthplace || f.birthplace,
        death_year: s.death_year || f.death_year,
        death_place: s.death_place || f.death_place,
        auschwitz_number: s.auschwitz_number || f.auschwitz_number,
        interview_date: s.interview_date || f.interview_date,
        interviewer: s.interviewer || f.interviewer,
        interview_place: s.interview_place || f.interview_place,
        languages: s.languages || f.languages,
        religion: s.religion || f.religion,
        prewar_address: s.prewar_address || f.prewar_address,
        notes: s.notes || f.notes,
        name_phonetic: s.name_phonetic || f.name_phonetic,
        nationality: s.nationality || f.nationality,
        mother_tongue: s.mother_tongue || f.mother_tongue,
        father_name: s.father_name || f.father_name,
        mother_name: s.mother_name || f.mother_name,
        mother_maiden_name: s.mother_maiden_name || f.mother_maiden_name,
        num_siblings: s.num_siblings || f.num_siblings,
        confidence: 'ai_draft',
      }));
    }
    axios.get(`${backendUrl}/subject/places?root=${encodeURIComponent(root)}`, authConfig)
      .then(pr => { if (pr) setPlaces(pr.data); }).catch(() => {});
  };

  const applySeedPreview = (accepts) => {
    setSeedApplying(true);
    axios.post(`${backendUrl}/subject/seed/apply?root=${encodeURIComponent(root)}`, accepts, authConfig)
      .then(res => {
        const r = res.data;
        _applySeedResult(r);
        const msg = `✓ Applied — ${r.places_inserted || 0} places, ${r.family_inserted || 0} family, ${r.timeline_inserted || 0} timeline`;
        if (pushToast) pushToast('seed-apply', 'success', msg);
        setSeedPreview(null);
      })
      .catch(e => {
        const msg = e?.response?.data?.detail || e?.message || 'Apply failed';
        if (pushToast) pushToast('seed-apply', 'error', msg);
      })
      .finally(() => setSeedApplying(false));
  };

  const seed = () => {
    if (!root) {
      setErr('Select an interview before running transcript extraction.');
      return;
    }
    setSeeding(true); setErr(null);
    if (onSeedStart) onSeedStart(root);
    axios.post(`${backendUrl}/subject/seed?root=${encodeURIComponent(root)}`, {}, authConfig)
      .then(res => {
        const jobId = res.data?.job_id;
        if (!jobId) {
          // Old-style sync response (shouldn't happen)
          const seedSummary = `✓ Extraction complete — ${res.data.places_extracted || 0} locations, ${res.data.family_inserted ?? 0} family members, ${res.data.timeline_inserted ?? 0} timeline events`;
          if (onSeedDone) onSeedDone(root, 'success', seedSummary);
          setSeeding(false);
          return;
        }
        // Poll the background job
        const poll = () => {
          axios.get(`${backendUrl}/jobs/${jobId}`)
            .then(jr => {
              if (jr.data.status === 'running') {
                setTimeout(poll, 3000);
                return;
              }
              if (jr.data.status === 'complete') {
                const r = jr.data.result || {};
                // Re-seed returns preview for review
                if (r.status === 'preview') {
                  setSeedPreview(r);
                  if (onSeedDone) onSeedDone(root, 'success', '✓ Extraction complete — review proposed changes');
                  setSeeding(false);
                  return;
                }
                const seedSummary = `✓ Extraction complete — ${r.places_extracted || 0} locations, ${r.family_inserted ?? 0} family members, ${r.timeline_inserted ?? 0} timeline events`;
                if (onSeedDone) onSeedDone(root, 'success', seedSummary);
                _applySeedResult(r);
              } else {
                const errMsg = jr.data.error || 'Unknown error';
                setErr(errMsg);
                if (onSeedDone) onSeedDone(root, 'error', errMsg);
              }
              setSeeding(false);
            })
            .catch(() => setTimeout(poll, 5000));
        };
        setTimeout(poll, 3000);
      })
      .catch(e => {
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : (e?.message || 'Transcript extraction failed.');
        setErr(msg);
        if (onSeedDone) onSeedDone(root, 'error', msg);
        setSeeding(false);
      });
  };

  React.useImperativeHandle(ref, () => ({
    triggerSeed: seed,
  }), [seed]);

  React.useEffect(() => {
    if (!onSeedStateChange) return;
    onSeedStateChange({ seeding, lastSeededAt });
  }, [seeding, lastSeededAt, onSeedStateChange]);

  const save = () => {
    setSaving(true); setErr(null);
    const saves = [
      axios.put(`${backendUrl}/subject?root=${encodeURIComponent(root)}`, form, authConfig),
    ];
    if (interviewLang.trim()) {
      saves.push(
        axios.put(`${backendUrl}/pipeline/language`, {
          project_root: root,
          language: interviewLang.trim(),
          confirmed: true,
        }, authConfig)
      );
    }
    Promise.all(saves)
      .then(([subjectRes]) => {
        onSaved(subjectRes.data);
        // The biography has no auto-save, so the only prior signal it worked
        // was the spinner stopping. Say it plainly.
        const who = (form.display_name || '').trim();
        toast('subject-saved', 'success', who ? `Saved ${who}'s record` : 'Subject record saved');
      })
      .catch(e => {
        const detail = e?.response?.data?.detail || e.message;
        setErr(detail);
        toast('subject-save-err', 'error', `Could not save: ${detail}`);
      })
      .finally(() => setSaving(false));
  };

  const confidenceBadge = {
    ai_draft: { label: 'AI Draft — Needs Review', icon: Bot,         color: '#f59e0b' },
    verified: { label: 'Verified',                icon: UserCheck,   color: '#34c759' },
    published:{ label: 'Published',               icon: Megaphone,   color: '#007aff' },
  }[form.confidence] || { label: form.confidence, icon: AlertCircle, color: theme.textSub };
  const ConfIcon = confidenceBadge.icon;
  const activeTab = externalTab ?? tab;
  const setActiveTab = onTabChange ?? setTab;
  const bioCentered = fullscreen && activeTab === 'bio';
  const contentOverflowY = activeTab === 'bio' ? 'auto' : 'hidden';
  const fullscreenPaneHeight = fullscreen
    ? (hideInternalTabs ? 'calc(100vh - 248px)' : 'calc(100vh - 300px)')
    : 'calc(100vh - 340px)';

  // Show the free-text box when the user asked for "Other…", or when the stored
  // language simply isn't in the known list yet (so an unusual existing value
  // stays visible and editable instead of the select blanking it out).
  const showLangFreeText = interviewLangOther
    || (!!interviewLang && knownLangs.length > 0 && !knownLangs.includes(interviewLang));

  const languagePanel = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 14px', background: isDarkMode ? 'rgba(0,122,255,0.08)' : 'rgba(0,122,255,0.06)', borderRadius: '10px', border: `1px solid ${isDarkMode ? 'rgba(0,122,255,0.25)' : 'rgba(0,122,255,0.2)'}` }}>
        <label style={{ fontSize: '0.66rem', fontWeight: 700, color: '#007aff', letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0 }}>
          Interview Language
        </label>
        {/* Native <select> rather than a <datalist>: Safari on iPad never renders
            datalist suggestions, so an iPad user got a bare free-text box with no
            idea which languages exist. "Other…" keeps free-text entry for a
            language that isn't in the list yet. */}
        {!showLangFreeText ? (
          <Select
            theme={theme}
            fullWidth={false}
            value={knownLangs.includes(interviewLang) ? interviewLang : ''}
            onChange={e => {
              if (e.target.value === '__other__') { setInterviewLangOther(true); setInterviewLang(''); }
              else setInterviewLang(e.target.value);
            }}
            aria-label="Interview Language"
            style={{ flex: 1, border: `1px solid ${interviewLang ? '#007aff' : theme.border}`, borderRadius: '7px', fontWeight: interviewLang ? 600 : 400 }}
          >
            <option value="">Language…</option>
            {[...knownLangs].sort((a, b) => a.localeCompare(b)).map(l => (
              <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
            ))}
            <option value="__other__">Other…</option>
          </Select>
        ) : (
          <ClearableField
            theme={theme}
            value={interviewLang}
            onChange={e => setInterviewLang(e.target.value)}
            onClear={() => { setInterviewLang(''); setInterviewLangOther(false); }}
            placeholder="e.g. Swedish"
            autoComplete="off"
            aria-label="Interview Language"
            style={{ flex: 1, padding: '7px 10px', background: theme.inputBg || theme.card, color: theme.text, border: `1px solid ${interviewLang ? '#007aff' : theme.border}`, borderRadius: '7px', fontSize: '0.84rem', outline: 'none', boxSizing: 'border-box', fontWeight: interviewLang ? 600 : 400 }}
          />
        )}
        <button
          onClick={() => {
            if (!interviewLang.trim()) return;
            setInterviewLangSaving(true);
            axios.put(`${backendUrl}/pipeline/language`, { project_root: root, language: interviewLang.trim(), confirmed: true }, authConfig)
              .catch(() => setErr('Could not update interview language.'))
              .finally(() => setInterviewLangSaving(false));
          }}
          disabled={interviewLangSaving || !interviewLang.trim()}
          style={{ padding: '7px 14px', borderRadius: '7px', background: interviewLang.trim() ? '#007aff' : 'transparent', color: interviewLang.trim() ? '#fff' : theme.textSub, border: `1px solid ${interviewLang.trim() ? '#007aff' : theme.border}`, fontSize: '0.72rem', fontWeight: 700, cursor: interviewLang.trim() ? 'pointer' : 'default', flexShrink: 0, transition: '0.15s' }}>
          {interviewLangSaving ? '…' : 'Set'}
        </button>
      </div>
      <div style={{ background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '16px 18px', color: theme.textSub, fontSize: '0.82rem', lineHeight: 1.6 }}>
        Set the interview language separately from the subject biography. This value feeds pipeline metadata and should reflect the actual spoken interview language.
      </div>
    </div>
  );

  const statusRow = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '0.7rem', fontWeight: 700, color: confidenceBadge.color, background: `${confidenceBadge.color}20`, padding: '3px 10px', borderRadius: '20px', display:'flex', alignItems:'center', gap:'5px' }}>
        <ConfIcon size={11} strokeWidth={2} />{confidenceBadge.label}
      </span>
      <Select theme={theme} value={form.confidence} fullWidth={false}
        onChange={e => setForm(f => ({ ...f, confidence: e.target.value }))}
        style={{ fontSize: '0.7rem', padding: '3px 8px', background: theme.card, borderRadius: '999px' }}>
        <option value="ai_draft">AI Draft</option>
        <option value="verified">Verified</option>
        <option value="published">Published</option>
      </Select>
      {activeTab === 'places' && (
        <>
          <div style={{ width: '1px', height: '18px', background: theme.border, margin: '0 4px' }} />
          <button
            type="button"
            onClick={() => setPlaceTypeFilter('all')}
            style={{
              fontSize: '0.65rem',
              fontWeight: 700,
              padding: '3px 9px',
              borderRadius: '999px',
              border: `1px solid ${placeTypeFilter === 'all' ? '#64748b55' : '#64748b35'}`,
              background: placeTypeFilter === 'all' ? 'rgba(100,116,139,0.16)' : 'rgba(100,116,139,0.08)',
              color: '#64748b',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
            }}
          >
            <MapPin size={10} strokeWidth={2} /> All
          </button>
          {Object.entries(PLACE_TYPE_LABELS)
            .filter(([type]) => places.some(pl => pl.place_type === type))
            .map(([type, label]) => {
              const colour = PLACE_TYPE_COLOURS[type] || '#6b7280';
              const IconCmp = PLACE_TYPE_ICONS[type] || MapPin;
              const active = placeTypeFilter === type;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => togglePlaceTypeFilter(type)}
                  style={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    padding: '3px 9px',
                    borderRadius: '999px',
                    border: `1px solid ${active ? `${colour}55` : `${colour}35`}`,
                    background: active ? `${colour}22` : `${colour}12`,
                    color: colour,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                  }}
                >
                  <IconCmp size={10} strokeWidth={2} /> {label}
                </button>
              );
            })}
        </>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', boxSizing: 'border-box', flex: 1, minHeight: 0 }}>

      {/* ── Scrollable content ── */}
      <div style={{ flex: 1, overflowY: contentOverflowY, minHeight: 0, display: 'flex', flexDirection: 'column', overflowX: 'hidden', WebkitOverflowScrolling: 'touch' }}>

      <div style={{ width: '100%', maxWidth: bioCentered ? '960px' : 'none', margin: bioCentered ? '0 auto' : '0', display: 'flex', flexDirection: 'column', minHeight: 0, flex: activeTab === 'bio' ? '0 0 auto' : 1 }}>
      {/* Hide standalone statusRow on bio tab in fullscreen — it's in the Vital Statistics header */}
      {!(fullscreen && activeTab === 'bio') && statusRow}

      {/* Extract from Transcript — moved inside bio tab below */}
      </div>

      {!hideInternalTabs && (
      <>
      {languagePanel}

      {/* Tabs — migrated to the Tabs primitive. Active label is now contrastText
          of glossaryAccent, so it stays legible on every scheme. */}
      <Tabs
        theme={theme}
        value={activeTab}
        onChange={setActiveTab}
        activeColor={theme.glossaryAccent}
        style={{ marginBottom: '14px', marginTop: '4px' }}
        tabs={[
          { key: 'bio',      icon: User,      label: 'Biography' },
          { key: 'places',   icon: MapPin,    label: 'Places & Map', count: places.length },
          { key: 'family',   icon: GitBranch, label: 'Genogram', count: familyCount },
          { key: 'timeline', icon: Clock,     label: 'Timeline' },
        ]}
      />
      </>
      )}

      {activeTab === 'language' && !fullscreen && languagePanel}

      {activeTab === 'bio' && <div style={{ width: '100%', maxWidth: fullscreen ? '960px' : 'none', margin: fullscreen ? '0 auto' : '0', paddingTop: '22px', paddingBottom: '18px' }}>

        {/* ── Extract from Transcript + AI Costs — right-aligned above bio ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
          {/* AI cost pill (admin only, always visible) — click to expand breakdown */}
          {currentUser?.role === 'admin' && subjectCosts && (
            <button onClick={() => subjectCosts.total_runs > 0 && setShowCostBreakdown(p => !p)}
              style={{ fontSize: '0.62rem', fontWeight: 700, color: '#8b5cf6', background: isDarkMode ? 'rgba(139,92,246,0.12)' : 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', padding: '4px 10px', borderRadius: '8px', whiteSpace: 'nowrap', cursor: subjectCosts.total_runs > 0 ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: '4px', opacity: subjectCosts.total_runs > 0 ? 1 : 0.6 }}>
              AI Cost: ${(subjectCosts.total_cost || 0).toFixed(2)} · {subjectCosts.total_runs} runs
              {subjectCosts.total_runs > 0 && (showCostBreakdown ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <Clock size={12} strokeWidth={2.2} style={{ color: lastSeededAt ? '#3b82f6' : theme.textSub, flexShrink: 0 }} />
            <span style={{ fontSize: '0.68rem', color: lastSeededAt ? '#3b82f6' : theme.textSub }}>
              {lastSeededAt ? `Extracted ${fmtSeeded(lastSeededAt)}` : 'Not yet extracted'}
            </span>
          </div>
          {canEdit && (
            <button onClick={seed} disabled={seeding}
              style={{ padding: '6px 14px', background: seeding ? theme.card : '#1a1a2e', color: seeding ? theme.textSub : '#a78bfa', border: '1px dashed #7c3aed', borderRadius: '8px', fontWeight: 700, fontSize: '0.72rem', cursor: seeding ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {seeding ? <><Loader size={12} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} /> Extracting…</> : <><Bot size={12} strokeWidth={2} /> Extract from Transcript</>}
            </button>
          )}
        </div>

        {/* ── AI Cost Breakdown (expandable) ── */}
        {showCostBreakdown && subjectCosts && (
          <div style={{ background: isDarkMode ? 'rgba(139,92,246,0.06)' : 'rgba(139,92,246,0.04)', border: '1px solid rgba(139,92,246,0.15)', borderRadius: '10px', padding: '12px 14px', marginBottom: '12px' }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#8b5cf6', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cost Breakdown</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '6px', marginBottom: '10px' }}>
              {[
                { label: 'QA Review', val: subjectCosts.qa_cost },
                { label: 'Extraction', val: subjectCosts.seed_cost },
                { label: 'Transcription', val: subjectCosts.whisper_cost },
                { label: 'Translation', val: subjectCosts.translation_cost },
                { label: 'Clips', val: subjectCosts.clips_cost },
                { label: 'Places', val: subjectCosts.places_cost },
                { label: 'Relationships', val: subjectCosts.relationship_cost },
                { label: 'Timeline', val: subjectCosts.timeline_cost },
              ].filter(c => (c.val || 0) > 0).map(c => (
                <div key={c.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: theme.text, padding: '4px 8px', background: isDarkMode ? 'rgba(139,92,246,0.08)' : 'rgba(139,92,246,0.06)', borderRadius: '6px' }}>
                  <span style={{ color: theme.textSub }}>{c.label}</span>
                  <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>${(c.val || 0).toFixed(4)}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '16px', fontSize: '0.6rem', color: theme.textSub, borderTop: `1px solid ${theme.border}`, paddingTop: '6px' }}>
              <span>Total: <strong style={{ color: '#8b5cf6' }}>${(subjectCosts.total_cost || 0).toFixed(4)}</strong></span>
              <span>Runs: {subjectCosts.total_runs}</span>
              <span>Success: {subjectCosts.success_count}</span>
              {subjectCosts.total_tokens > 0 && <span>Tokens: {(subjectCosts.total_tokens || 0).toLocaleString()}</span>}
            </div>
            {subjectCosts.recent_runs?.length > 0 && (
              <details style={{ marginTop: '8px' }}>
                <summary style={{ fontSize: '0.6rem', color: theme.textSub, cursor: 'pointer' }}>Recent runs ({subjectCosts.recent_runs.length})</summary>
                <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '6px' }}>
                  {subjectCosts.recent_runs.map((r, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '0.58rem', color: theme.textSub, padding: '3px 0', borderBottom: `1px solid ${theme.border}` }}>
                      <span style={{ fontWeight: 600, minWidth: '80px' }}>{r.feature}</span>
                      <span style={{ color: r.status === 'success' || r.status === 'completed' ? theme.success : theme.danger }}>{r.status}</span>
                      <span style={{ fontFamily: 'monospace' }}>${(r.estimated_cost_usd || 0).toFixed(4)}</span>
                      <span>{r.model || ''}</span>
                      <span style={{ marginLeft: 'auto' }}>{r.finished_at ? new Date(r.finished_at).toLocaleDateString() : ''}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        {/* ── VITAL STATISTICS — non-negotiable opening fields ── */}
        <div style={{
          background: isDarkMode ? '#0a1628' : '#f0f6ff',
          border: `1.5px solid ${isDarkMode ? '#1e3a5f' : '#bfdbfe'}`,
          borderRadius: '10px',
          padding: '14px',
          marginBottom: '14px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '12px' }}>
            <Shield size={13} strokeWidth={2.5} style={{ color: '#3b82f6', flexShrink: 0 }} />
            <span style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em', color: '#3b82f6', textTransform: 'uppercase' }}>
              Vital Statistics
            </span>
            <span style={{ fontSize: '0.65rem', color: isDarkMode ? '#4b7ab5' : '#93c5fd', marginLeft: '4px' }}>
              — from interviewee's own words only
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {/* Confidence badge + dropdown */}
              <span style={{ fontSize: '0.65rem', fontWeight: 700, color: confidenceBadge.color, background: `${confidenceBadge.color}20`, padding: '3px 8px', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <ConfIcon size={10} strokeWidth={2} />{confidenceBadge.label}
              </span>
              <Select theme={theme} value={form.confidence} fullWidth={false} size="sm"
                onChange={e => setForm(f => ({ ...f, confidence: e.target.value }))}
                style={{ fontSize: '0.62rem', padding: '2px 6px', background: theme.card, borderRadius: '999px' }}>
                <option value="ai_draft">AI Draft</option>
                <option value="verified">Verified</option>
                <option value="published">Published</option>
              </Select>
              <div style={{ width: '1px', height: '14px', background: theme.border }} />
              {interviewLang ? (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  padding: '3px 10px', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700,
                  background: interviewLangMeta.bg, color: interviewLangMeta.text,
                  border: `1px solid ${interviewLangMeta.border}`,
                }}>
                  <Globe size={11} />
                  {interviewLangMeta.label}
                </span>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <Globe size={11} style={{ color: '#007aff', flexShrink: 0 }} />
                  <Select
                    theme={theme}
                    fullWidth={false}
                    size="sm"
                    value=""
                    onChange={e => {
                      const val = e.target.value;
                      if (!val) return;
                      setInterviewLang(val);
                      setInterviewLangSaving(true);
                      axios.put(`${backendUrl}/pipeline/language`, { project_root: root, language: val.trim(), confirmed: true }, authConfig)
                        .catch(() => setErr('Could not update interview language.'))
                        .finally(() => setInterviewLangSaving(false));
                    }}
                    style={{ padding: '2px 6px', borderRadius: '999px', fontSize: '0.62rem' }}
                  >
                    <option value="">Interview Language…</option>
                    {knownLangs.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
                  </Select>
                  {interviewLangSaving && <Loader size={10} style={{ animation: 'spin 1s linear infinite', color: theme.textSub }} />}
                </span>
              )}
            </div>
          </div>

          {/* Row 1: Full Name + Spoken Name */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '12px', alignItems: 'start', marginBottom: '10px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '3px' }}>Full Name</label>
              <ClearableField
                theme={theme}
                value={form.display_name}
                onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                onClear={() => setForm(f => ({ ...f, display_name: '' }))}
                placeholder="e.g. Chaim David Szpigler"
                style={{ width: '100%', padding: '7px 9px', background: theme.inputBg || theme.card, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <label style={{ display: 'block', fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '3px', textAlign: 'center', width: '100%' }}>
                Spoken Name
              </label>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '34px', width: '100%' }}>
                {!pronunciationClip && typeof onOpenClips === 'function' && (
                  <button type="button" onClick={onOpenClips} title="Choose pronunciation clip in Clips"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 8px', borderRadius: '999px', border: `1px solid ${theme.border}`, background: theme.card, color: theme.textSub, fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                    Choose in Clips
                  </button>
                )}
                {pronunciationClip && (
                  <button type="button" onClick={playPronunciation} title="Play pronunciation" disabled={!canPlayPronunciation}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', borderRadius: '999px', border: `1px solid ${playingPronunciation ? theme.glossaryAccent : theme.border}`, background: playingPronunciation ? `${theme.glossaryAccent}15` : theme.card, cursor: canPlayPronunciation ? 'pointer' : 'default', opacity: canPlayPronunciation ? 1 : 0.45, flexShrink: 0 }}>
                    <AppLogoMark fallbackSize={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Hebrew + Pronunciation + IPA + Tool button */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '8px', alignItems: 'end', marginBottom: '12px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px' }}>
                <label style={{ fontSize: '0.62rem', fontWeight: 700, color: '#d97706', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Hebrew Script</label>
                {form.name_hebrew && (
                  <button type="button" onClick={() => playFieldAudio(form.name_hebrew, 'hebrew')} title="Listen"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', display: 'flex', color: fieldPlaying === 'hebrew' ? '#22c55e' : '#d97706', opacity: fieldPlaying === 'hebrew' ? 1 : 0.6 }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => { if (fieldPlaying !== 'hebrew') e.currentTarget.style.opacity = '0.6'; }}>
                    <Play size={10} />
                  </button>
                )}
              </div>
              <ClearableField
                theme={theme}
                value={form.name_hebrew}
                onChange={e => setForm(f => ({ ...f, name_hebrew: e.target.value }))}
                onClear={() => setForm(f => ({ ...f, name_hebrew: '' }))}
                placeholder="e.g. חיים דוד שפיגלר"
                style={{ width: '100%', padding: '7px 9px', background: theme.inputBg || theme.card, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box', direction: 'rtl', fontFamily: "'Noto Sans Hebrew', 'Arial Hebrew', sans-serif" }}
              />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px' }}>
                <label style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Pronunciation</label>
                {form.name_phonetic && (
                  <button type="button" onClick={() => playFieldAudio(form.name_phonetic, 'phonetic')} title="Listen"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', display: 'flex', color: fieldPlaying === 'phonetic' ? '#22c55e' : '#8b5cf6', opacity: fieldPlaying === 'phonetic' ? 1 : 0.6 }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => { if (fieldPlaying !== 'phonetic') e.currentTarget.style.opacity = '0.6'; }}>
                    <Play size={10} />
                  </button>
                )}
              </div>
              <ClearableField
                theme={theme}
                value={form.name_phonetic}
                onChange={e => setForm(f => ({ ...f, name_phonetic: e.target.value }))}
                onClear={() => setForm(f => ({ ...f, name_phonetic: '' }))}
                placeholder="e.g. KY-im DAH-vid SHPIG-ler"
                style={{ width: '100%', padding: '7px 9px', background: theme.inputBg || theme.card, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px' }}>
                <label style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.05em', textTransform: 'uppercase' }}>IPA Notation</label>
                {form.name_ipa && (
                  <button type="button" onClick={() => cloudTtsAvailable && playFieldAudio(form.name_ipa, 'ipa')} disabled={!cloudTtsAvailable} title={cloudTtsAvailable ? 'Listen' : 'Cloud TTS not configured'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', display: 'flex', color: fieldPlaying === 'ipa' ? '#22c55e' : '#3b82f6', opacity: fieldPlaying === 'ipa' ? 1 : 0.6 }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => { if (fieldPlaying !== 'ipa') e.currentTarget.style.opacity = '0.6'; }}>
                    <Play size={10} />
                  </button>
                )}
              </div>
              <ClearableField
                theme={theme}
                value={form.name_ipa}
                onChange={e => setForm(f => ({ ...f, name_ipa: e.target.value }))}
                onClear={() => setForm(f => ({ ...f, name_ipa: '' }))}
                placeholder="e.g. /ˈχɑɪm ˈdɑ.vɪd ˈʃpɪɡ.lər/"
                style={{ width: '100%', padding: '7px 9px', background: theme.inputBg || theme.card, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ paddingBottom: '1px' }}>
              <button type="button" onClick={() => setShowPronunciationTool(v => !v)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '6px 10px', borderRadius: '6px',
                  background: showPronunciationTool ? `${theme.glossaryAccent}15` : 'transparent',
                  border: `1px solid ${showPronunciationTool ? theme.glossaryAccent : theme.border}`,
                  color: showPronunciationTool ? theme.glossaryAccent : theme.textSub,
                  fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', height: '34px',
                }}>
                <Volume2 size={10} /> {showPronunciationTool ? '▴' : '▾'}
              </button>
            </div>
          </div>

          {/* ── Pronunciation Tuner Panel ── */}
          {showPronunciationTool && (
            <div style={{
              marginBottom: '12px', padding: '14px', borderRadius: '10px',
              background: isDarkMode ? 'rgba(139,92,246,0.06)' : 'rgba(139,92,246,0.04)',
              border: '1px solid rgba(139,92,246,0.15)',
            }}>
              <div style={{ fontSize: '0.68rem', color: theme.textSub, marginBottom: '12px' }}>
                Tune the pronunciation so AI narrators (NotebookLM, podcasts) say the name correctly.
              </div>

              {/* Step 1: Reference — how the name actually sounds */}
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '0.6rem', fontWeight: 800, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                  1. Reference — How it should sound
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {pronunciationClip && (
                    <Button theme={theme} variant="success" size="sm" icon={Play}
                      onClick={playPronunciation} disabled={!canPlayPronunciation}>
                      Name Clip (Tape {pronunciationClip.part_number})
                    </Button>
                  )}
                  {hasUserRecording && (
                    <button type="button"
                      onClick={() => {
                        const url = `${backendUrl}/subject/pronunciation-recording?root=${encodeURIComponent(root)}`;
                        axios.get(url, { ...authConfig, responseType: 'blob' })
                          .then(({ data }) => { const a = new Audio(); a.src = URL.createObjectURL(data); a.play(); a.onended = () => URL.revokeObjectURL(a.src); })
                          .catch(() => {});
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px', borderRadius: '8px', background: '#3b82f6', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700 }}>
                      <Play size={12} /> My Recording
                    </button>
                  )}
                  <button type="button" onClick={recording ? stopRecording : startRecording}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px', borderRadius: '8px', background: recording ? '#ef4444' : theme.card, color: recording ? '#fff' : theme.text, border: `1px solid ${recording ? '#ef4444' : theme.border}`, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700 }}>
                    {recording ? <><MicOff size={12} /> Stop</> : <><Mic size={12} /> Record Yourself</>}
                  </button>
                  {!pronunciationClip && typeof onOpenClips === 'function' && (
                    <Button theme={theme} variant="outline" size="sm" icon={Scissors} onClick={onOpenClips}>
                      Set Name Clip
                    </Button>
                  )}
                </div>
                {recordedUrl && !recording && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                    <audio src={recordedUrl} controls style={{ height: '32px' }} />
                    {/* The recording already uploaded itself when you stopped
                        recording, so this is a RETRY, not the thing that saves.
                        Labelling it "Save" implied the recording was unsaved
                        until pressed, which was the confusing part (owner
                        decision 2026-07-19: keep the auto-save, relabel this). */}
                    <Button theme={theme} variant="secondary" size="sm" onClick={uploadRecording}
                      loading={uploadingRecording} icon={RotateCcw}
                      title="The recording is uploaded automatically. Use this to upload it again if that failed.">
                      {uploadingRecording ? 'Uploading…' : 'Upload again'}
                    </Button>
                    <Button theme={theme} variant="danger-ghost" size="sm" icon={Trash2} title="Discard"
                      onClick={() => { setRecordedBlob(null); if (recordedUrl) try { URL.revokeObjectURL(recordedUrl); } catch {} setRecordedUrl(null); }} />
                  </div>
                )}
              </div>

              {/* Step 2: AI Suggestions — IPA + Hebrew */}
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '0.6rem', fontWeight: 800, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                  2. AI Suggestions
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                  <button type="button" disabled={suggestingIpa || !form.display_name}
                    onClick={() => {
                      setSuggestingIpa(true);
                      axios.post(`${backendUrl}/subject/pronunciation-suggest-ipa`, {
                        name: form.display_name, phonetic: form.name_phonetic, project_root: root,
                      }, authConfig)
                        .then(r => {
                          if (r.data?.ipa) {
                            setForm(f => {
                              const updated = { ...f, name_ipa: r.data.ipa };
                              // Auto-save
                              axios.put(`${backendUrl}/subject?root=${encodeURIComponent(root)}`, updated, authConfig).catch(() => {});
                              return updated;
                            });
                          }
                        })
                        .catch(e => toast('pron-suggest-ipa', 'error', e?.response?.data?.detail || 'IPA suggestion failed'))
                        .finally(() => setSuggestingIpa(false));
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '7px 14px', borderRadius: '6px', background: suggestingIpa ? theme.card : '#8b5cf6', color: suggestingIpa ? theme.textSub : '#fff', border: 'none', cursor: suggestingIpa ? 'wait' : 'pointer', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {suggestingIpa ? <><Loader size={11} /> Suggesting...</> : <><Sparkles size={11} /> Suggest IPA</>}
                  </button>
                  <button type="button" disabled={suggestingIpa || !form.display_name}
                    onClick={() => {
                      setSuggestingIpa(true);
                      axios.post(`${backendUrl}/subject/pronunciation-suggest-hebrew`, {
                        name: form.display_name, project_root: root,
                      }, authConfig)
                        .then(r => {
                          if (r.data?.hebrew) {
                            setForm(f => {
                              const updated = { ...f, name_hebrew: r.data.hebrew };
                              axios.put(`${backendUrl}/subject?root=${encodeURIComponent(root)}`, updated, authConfig).catch(() => {});
                              return updated;
                            });
                          }
                        })
                        .catch(e => toast('pron-suggest-hebrew', 'error', e?.response?.data?.detail || 'Hebrew suggestion failed'))
                        .finally(() => setSuggestingIpa(false));
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '7px 14px', borderRadius: '6px', background: suggestingIpa ? theme.card : '#d97706', color: suggestingIpa ? theme.textSub : '#fff', border: 'none', cursor: suggestingIpa ? 'wait' : 'pointer', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {suggestingIpa ? <><Loader size={11} /> Suggesting...</> : <><Sparkles size={11} /> Suggest Hebrew</>}
                  </button>
                </div>
                <div style={{ fontSize: '0.58rem', color: theme.textSub }}>
                  IPA: χ = KH (guttural) · ʃ = SH · ɪ = short i · ɑ = ah · ˈ = stress &nbsp;|&nbsp; Hebrew script enables accurate pronunciation with Hebrew voices
                </div>
              </div>

              {/* Step 3: Test with AI Voice — Hebrew first (best), then Gemini, then Cloud IPA */}
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '0.6rem', fontWeight: 800, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                  3. Test with AI Voice
                </div>

                {/* Voice selector — shared across all engines */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textSub }}>Voice:</span>
                  <Select theme={theme} fullWidth={false} size="sm" value={geminiVoice}
                    onChange={e => { setGeminiVoice(e.target.value); setGeminiAudioUrl(null); }}
                    style={{ padding: '5px 8px', fontSize: '0.72rem' }}>
                    {['Kore', 'Puck', 'Charon', 'Fenrir', 'Leda', 'Orus', 'Zephyr', 'Aoede'].map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </Select>
                  {geminiAudioUrl && <audio src={geminiAudioUrl} controls autoPlay style={{ height: '30px', flex: 1, minWidth: '120px' }} />}
                </div>

                {/* Hebrew voice — best for Ashkenazi names */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                  {form.name_hebrew ? (
                    <button type="button" disabled={geminiGenerating}
                      onClick={() => {
                        setGeminiGenerating(true); setGeminiAudioUrl(null);
                        axios.post(`${backendUrl}/subject/pronunciation-preview`, {
                          name: form.name_hebrew, phonetic: '', ipa: '', voice: geminiVoice, project_root: root,
                        }, { ...authConfig, responseType: 'blob' })
                          .then(({ data }) => {
                            if (geminiAudioUrl) try { URL.revokeObjectURL(geminiAudioUrl); } catch {}
                            setGeminiAudioUrl(URL.createObjectURL(data));
                          })
                          .catch(e => toast('pron-tts-hebrew', 'error', e?.response?.data?.detail || 'Hebrew TTS failed'))
                          .finally(() => setGeminiGenerating(false));
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px', borderRadius: '7px', background: geminiGenerating ? theme.card : '#d97706', color: geminiGenerating ? theme.textSub : '#fff', border: 'none', cursor: geminiGenerating ? 'wait' : 'pointer', fontSize: '0.75rem', fontWeight: 700 }}>
                      {geminiGenerating ? <><Loader size={11} /> ...</> : <><Play size={12} /> Hebrew: {form.name_hebrew}</>}
                    </button>
                  ) : (
                    <span style={{ fontSize: '0.65rem', color: theme.textSub, fontStyle: 'italic' }}>Fill in Hebrew Script above for the best pronunciation</span>
                  )}
                  <span style={{ fontSize: '0.55rem', color: '#d97706', fontWeight: 600 }}>Recommended</span>
                </div>

                {/* Cloud TTS with IPA */}
                {form.name_ipa && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                    <button type="button" disabled={geminiGenerating || !cloudTtsAvailable}
                      title={cloudTtsAvailable ? 'Play with IPA pronunciation' : 'Google Cloud service account required'}
                      onClick={() => {
                        setGeminiGenerating(true); setGeminiAudioUrl(null);
                        axios.post(`${backendUrl}/subject/pronunciation-cloud-tts`, {
                          name: form.display_name, ipa: form.name_ipa,
                          voice: 'en-US-Neural2-D', lang: 'en-US', project_root: root,
                        }, { ...authConfig, responseType: 'blob' })
                          .then(({ data }) => {
                            if (geminiAudioUrl) try { URL.revokeObjectURL(geminiAudioUrl); } catch {}
                            setGeminiAudioUrl(URL.createObjectURL(data));
                          })
                          .catch(e => toast('pron-tts-cloud', 'error', e?.response?.data?.detail || 'Cloud TTS needs Google Cloud service account'))
                          .finally(() => setGeminiGenerating(false));
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 12px', borderRadius: '7px', background: !cloudTtsAvailable ? '#94a3b8' : geminiGenerating ? theme.card : '#3b82f6', color: !cloudTtsAvailable ? '#cbd5e1' : geminiGenerating ? theme.textSub : '#fff', border: 'none', cursor: !cloudTtsAvailable ? 'not-allowed' : geminiGenerating ? 'wait' : 'pointer', fontSize: '0.72rem', fontWeight: 700, opacity: cloudTtsAvailable ? 1 : 0.5 }}>
                      <Play size={11} /> {cloudTtsAvailable ? `Cloud IPA: ${form.name_ipa}` : 'Cloud IPA (not configured)'}
                    </button>
                  </div>
                )}

                <div style={{ fontSize: '0.55rem', color: theme.textSub, marginTop: '4px' }}>
                  <strong style={{ color: '#d97706' }}>Hebrew</strong> gives the most accurate pronunciation for Ashkenazi names.
                  <strong style={{ color: '#8b5cf6' }}> English</strong> uses the phonetic guide.
                  <strong style={{ color: '#3b82f6' }}> Cloud IPA</strong> uses SSML phoneme tags (requires Google Cloud credentials).
                </div>
              </div>

              {/* Step 4: Copy for AI tools */}
              {(form.name_phonetic || form.name_ipa) && (
                <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '0.6rem', fontWeight: 800, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', width: '100%', marginBottom: '2px' }}>
                    4. Copy for AI Tools
                  </div>
                  {form.name_ipa && (
                    <Button theme={theme} variant="secondary" size="sm" icon={Edit3}
                      onClick={() => navigator.clipboard.writeText(`Pronunciation of "${form.display_name}": ${form.name_ipa}`)}>
                      Copy IPA
                    </Button>
                  )}
                  {form.name_phonetic && (
                    <Button theme={theme} variant="secondary" size="sm" icon={Edit3}
                      onClick={() => navigator.clipboard.writeText(`Pronounce "${form.display_name}" as: ${form.name_phonetic}`)}>
                      Copy Phonetic Guide
                    </Button>
                  )}
                  <div style={{ fontSize: '0.55rem', color: theme.textSub, width: '100%' }}>
                    Paste into NotebookLM prompts, podcast scripts, or AI narrator instructions.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Row 2: Birth + Death */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <HistoricalDatePicker
                label="Date of Birth"
                value={form.born}
                onChange={v => setForm(f => ({ ...f, born: v }))}
                theme={theme}
                minYear={1860}
                maxYear={2010}
                showSummary={false}
              />
            </div>
            <div style={{ flex: 2 }}>{field('birthplace', 'Birthplace', 'e.g. Będzin, Poland')}</div>
            <div style={{ flex: 1 }}>
              <HistoricalDatePicker
                label="Died"
                value={form.death_year}
                onChange={v => setForm(f => ({ ...f, death_year: v }))}
                theme={theme}
                minYear={1860}
                maxYear={new Date().getFullYear()}
                showSummary={false}
              />
            </div>
            <div style={{ flex: 2 }}>{field('death_place', 'Place of Death', 'e.g. Stockholm, Sweden')}</div>
          </div>

          {/* Row 3: Nationality + Mother tongue */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>{field('nationality', 'Pre-war Nationality', 'e.g. Polish')}</div>
            <div style={{ flex: 1 }}>{field('mother_tongue', 'Mother Tongue', 'e.g. Yiddish')}</div>
          </div>

          {/* Row 4: Father + Mother */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>{field('father_name', "Father's Name", 'e.g. Ojzer Szpigler')}</div>
            <div style={{ flex: 1 }}>{field('mother_name', "Mother's Name", 'e.g. Miriam Szpigler')}</div>
          </div>

          {/* Row 5: Mother maiden + Siblings */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>{field('mother_maiden_name', "Mother's Maiden Name", 'e.g. Schweitzer')}</div>
            <div style={{ flex: 1 }}>{field('num_siblings', 'Siblings', 'e.g. 3 sisters')}</div>
          </div>
        </div>

        {/* ── ADDITIONAL BIOGRAPHY FIELDS ── */}
        {field('prewar_address', 'Pre-war Address', 'e.g. Kollontaj 9, Będzin')}
        {field('auschwitz_number', 'Auschwitz Prisoner No.', 'e.g. 135098')}
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1 }}>{field('religion', 'Religion / Denomination', 'e.g. Orthodox Jewish')}</div>
          <div style={{ flex: 1 }}>{field('languages', 'All Languages Spoken', 'e.g. Yiddish, Polish, Swedish')}</div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 2 }}>{field('interview_date', 'Interview Date', 'e.g. 20 May 1997')}</div>
          <div style={{ flex: 2 }}>{field('interviewer', 'Interviewer', 'e.g. Jan Levy')}</div>
          {/* Recorded at intake. Without a field here it could be written once
              and never corrected. Distinct from birthplace, which is the person. */}
          <div style={{ flex: 2 }}>{field('interview_place', 'Interview Location', 'e.g. Stockholm, Sweden')}</div>
        </div>

        {/* ── ARCHIVAL BIOGRAPHY NOTES — flex fills remaining space ── */}
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <label style={{ display: 'block', fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '3px' }}>
            Archival Biography Notes
          </label>
          <Input
            multiline
            theme={theme}
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Biographical summary for archive record…"
            rows={10}
            style={{ flex: 1, minHeight: '220px' }}
          />
        </div>

        {/* Save button — right below notes */}
        <Button theme={theme} variant="primary" size="md" fullWidth onClick={save} disabled={saving}
          style={{ marginTop: '12px' }}>
          {saving ? 'Saving…' : 'Save Subject Record'}
        </Button>
      </div>}

      {activeTab === 'places' && (
        <div style={{ display: 'flex', gap: '14px', minHeight: 0, height: fullscreenPaneHeight, maxHeight: fullscreenPaneHeight, flex: '0 0 auto', overflow: 'hidden', alignItems: 'stretch' }}>
          <div style={{ flex: '0 0 34%', minWidth: '300px', maxWidth: '420px', display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden' }}>
            <div style={{
              fontSize: '0.7rem',
              color: theme.textSub,
              marginBottom: '10px',
              display:'flex',
              alignItems:'flex-start',
              gap:'8px',
              background: isDarkMode ? 'rgba(86, 142, 255, 0.10)' : '#eef5ff',
              border: `1px solid ${isDarkMode ? 'rgba(122, 162, 255, 0.32)' : '#cfe0ff'}`,
              borderLeft: `4px solid ${theme.glossaryAccent || '#3b82f6'}`,
              borderRadius: '10px',
              padding: '11px 13px',
              lineHeight: 1.45,
              boxShadow: isDarkMode ? 'none' : '0 1px 0 rgba(31, 41, 55, 0.04)',
            }}>
              <AlertCircle size={13} strokeWidth={2} style={{flexShrink:0, marginTop:'1px'}} />
              <div>
                <div style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.text, marginBottom: '2px' }}>
                  Extracted Locations
                </div>
                <div>
                  Locations extracted from transcript. Use Extract from Transcript to populate. Verify coordinates and years before publishing.
                </div>
              </div>
            </div>
            {/* Merge action bar + AI review */}
            {canEdit && places.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                {placeMergeSelection.size >= 2 && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '6px', flex: 1,
                    padding: '5px 10px', borderRadius: '8px',
                    background: '#16a34a14', border: '1px solid #16a34a44',
                  }}>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#16a34a' }}>
                      {placeMergeSelection.size} selected
                    </span>
                    <Button theme={theme} variant="success" size="sm" icon={GitMerge}
                      onClick={handlePlaceMerge} disabled={placeMerging}>
                      {placeMerging ? 'Merging…' : 'Merge'}
                    </Button>
                    <Button theme={theme} variant="outline" size="sm" onClick={() => setPlaceMergeSelection(new Set())}>
                      Cancel
                    </Button>
                  </div>
                )}
                <button onClick={handlePlaceEnrichFromFamily} disabled={placeEnriching}
                  style={{
                    marginLeft: 'auto', padding: '4px 10px', borderRadius: '6px',
                    background: placeEnriching ? theme.card : (isDarkMode ? 'rgba(22,163,106,0.12)' : 'rgba(22,163,106,0.06)'),
                    color: placeEnriching ? theme.textSub : '#16a34a',
                    border: `1px solid ${placeEnriching ? theme.border : '#16a34a44'}`,
                    fontSize: '0.64rem', fontWeight: 700, cursor: placeEnriching ? 'not-allowed' : 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                  }}>
                  {placeEnriching ? <><Loader size={10} style={{ animation: 'spin 1s linear infinite' }} />Enriching…</> : <><GitBranch size={11} />Enrich from Family</>}
                </button>
                <button onClick={handlePlaceAiReview} disabled={placeAiReviewing}
                  style={{
                    padding: '4px 10px', borderRadius: '6px',
                    background: placeAiReviewing ? theme.card : (isDarkMode ? 'rgba(124,58,237,0.15)' : 'rgba(124,58,237,0.08)'),
                    color: placeAiReviewing ? theme.textSub : '#7c3aed',
                    border: `1px solid ${placeAiReviewing ? theme.border : '#7c3aed44'}`,
                    fontSize: '0.64rem', fontWeight: 700, cursor: placeAiReviewing ? 'not-allowed' : 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                  }}>
                  {placeAiReviewing ? <><Loader size={10} style={{ animation: 'spin 1s linear infinite' }} />Reviewing…</> : <><Sparkles size={11} />AI Review</>}
                </button>
              </div>
            )}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', paddingRight: '4px', overscrollBehavior: 'contain' }}>
              {places.length === 0
                ? <div style={{ textAlign: 'center', padding: '20px', color: theme.textSub, fontSize: '0.8rem', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '10px' }}>
                    No places yet — use Extract from Transcript to extract from transcript.
                  </div>
                : filteredPlaces.length === 0
                  ? <div style={{ textAlign: 'center', padding: '20px', color: theme.textSub, fontSize: '0.8rem', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '10px' }}>
                      No places match the current legend filter.
                    </div>
                  : filteredPlaces.map(pl => (
                  <div
                    key={pl.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => pl.lat && pl.lon && setFocusedPlaceId(pl.id)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pl.lat && pl.lon && setFocusedPlaceId(pl.id); } }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      background: theme.card,
                      border: `1px solid ${focusedPlaceId === pl.id ? theme.glossaryAccent || '#007aff' : (pl.verified ? '#34c759' : theme.border)}`,
                      borderRadius: '8px',
                      padding: '7px 9px',
                      marginBottom: '5px',
                      cursor: pl.lat && pl.lon ? 'pointer' : 'default',
                      boxSizing: 'border-box',
                    }}
                  >
                    {(() => {
                      const colour = PLACE_TYPE_COLOURS[pl.place_type] || '#6b7280';
                      const PlaceIcon = PLACE_TYPE_ICONS[pl.place_type] || MapPin;
                      const placeNumber = placeNumberById[pl.id] || null;
                      return (
                        <>
                    {/* ── Row 1: checkbox + number + place name + place type (flush right) ── */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {canEdit && (
                      <div
                        role="checkbox"
                        tabIndex={0}
                        aria-checked={placeMergeSelection.has(pl.id)}
                        onClick={e => { e.stopPropagation(); togglePlaceMerge(pl.id); }}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); togglePlaceMerge(pl.id); } }}
                        style={{
                          width: '14px', height: '14px', borderRadius: '3px', flexShrink: 0,
                          border: `1.5px solid ${placeMergeSelection.has(pl.id) ? '#16a34a' : theme.border}`,
                          background: placeMergeSelection.has(pl.id) ? '#16a34a' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        }}
                      >
                        {placeMergeSelection.has(pl.id) && <Check size={9} strokeWidth={3} color="#fff" />}
                      </div>
                      )}
                      {placeNumber && (
                        <span style={{
                          width: '18px', height: '18px', borderRadius: '999px', background: '#fff',
                          color: colour, border: `1.5px solid ${colour}`, fontSize: '0.62rem', fontWeight: 800,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>{placeNumber}</span>
                      )}
                      <span style={{ fontWeight: 700, fontSize: '0.82rem', flex: 1, minWidth: 0 }}>
                        {pl.canonical || pl.place_name}
                      </span>
                      <span style={{ fontSize:'0.58rem', fontWeight:700, padding:'2px 6px',
                        borderRadius:'10px', background:`${colour}18`, color:colour, border:`1px solid ${colour}35`,
                        display:'inline-flex', alignItems:'center', gap:'3px', flexShrink: 0 }}>
                        <PlaceIcon size={9} strokeWidth={2} />
                        {PLACE_TYPE_LABELS[pl.place_type] || pl.place_type}
                      </span>
                    </div>
                    {/* ── Row 2: year (left, aligned with name) | DRAFT/verified + actions (right) ── */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '3px',
                      paddingLeft: `${14 + 6 + (placeNumber ? 18 + 6 : 0)}px` }}>
                      {/* Left: year, indented to align with name text */}
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {pl.year_from && (
                          <span style={{ fontSize: '0.62rem', color: theme.textSub }}>
                            {pl.year_from}{pl.year_to && pl.year_to !== pl.year_from ? `–${pl.year_to}` : ''}
                          </span>
                        )}
                      </div>
                      {/* Right: DRAFT/verified → action buttons, stacked & centered */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
                        {pl.verified
                          ? <Check size={14} strokeWidth={2.5} color="#34c759" />
                          : <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#f59e0b',
                              background: '#f59e0b20', padding: '1px 6px', borderRadius: '10px',
                              border: '1px solid #f59e0b35' }}>DRAFT</span>
                        }
                        {canEdit && (
                        <div style={{ display: 'flex', gap: '3px' }}>
                          <button onClick={(e) => { e.stopPropagation(); setEditingPlace({ ...pl }); }}
                            title="Edit place"
                            style={{ fontSize: '0.58rem', padding: '1px 4px', background: 'none', border: `1px solid ${theme.border}`, borderRadius: '4px', cursor: 'pointer', color: theme.textSub }}>
                            <Edit3 size={9} strokeWidth={2} />
                          </button>
                          <button onClick={(e) => {
                            e.stopPropagation();
                            axios.put(`${backendUrl}/subject/places/${pl.id}?root=${encodeURIComponent(root)}`, { ...pl, verified: !pl.verified }, authConfig)
                              .then(() => setPlaces(prev => prev.map(p => p.id === pl.id ? { ...p, verified: !p.verified } : p)));
                          }} title={pl.verified ? 'Unverify' : 'Verify'}
                            style={{ fontSize: '0.58rem', padding: '1px 4px', background: 'none', border: `1px solid ${theme.border}`, borderRadius: '4px', cursor: 'pointer', color: theme.textSub }}>
                            {pl.verified ? <Check size={9} strokeWidth={2} /> : <Check size={9} strokeWidth={2} style={{ opacity: 0.3 }} />}
                          </button>
                          {canDeletePlaces && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!await confirm({ message: 'Delete this place card?', title: 'Delete place?', confirmLabel: 'Delete', destructive: true })) return;
                                axios.delete(`${backendUrl}/subject/places/${pl.id}?root=${encodeURIComponent(root)}`, authConfig)
                                  .then(() => {
                                    setPlaces(prev => prev.filter(p => p.id !== pl.id));
                                    setFocusedPlaceId(prev => (prev === pl.id ? null : prev));
                                  });
                              }}
                              title="Delete place"
                              style={{ fontSize: '0.58rem', padding: '1px 4px', background: 'none', border: '1px solid #ef444444', borderRadius: '4px', cursor: 'pointer', color: '#ef4444' }}
                            >
                              <Trash2 size={9} strokeWidth={2} />
                            </button>
                          )}
                        </div>
                        )}
                      </div>
                    </div>
                    {/* ── Row 2: context text + spoken name + source ── */}
                    {(pl.context || (pl.canonical && pl.canonical !== pl.place_name) || pl.source_locator) && (
                      <div style={{ marginTop: '3px', fontSize: '0.72rem', color: theme.text, lineHeight: 1.4 }}>
                        {pl.canonical && pl.canonical !== pl.place_name && (
                          <span style={{ color: theme.textSub, fontSize: '0.65rem' }}>
                            <span style={{
                              fontSize: '0.56rem', fontWeight: 700, padding: '1px 5px', borderRadius: '999px',
                              background: interviewLangMeta.bg, color: interviewLangMeta.text,
                              border: `1px solid ${interviewLangMeta.border}`, marginRight: '4px',
                            }}>{interviewLangMeta.label}</span>
                            "{pl.place_name}"
                            {pl.context ? ' — ' : ''}
                          </span>
                        )}
                        {pl.context && <span style={{ fontStyle: 'italic' }}>{pl.context}</span>}
                        {pl.source_locator && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: pl.context ? '6px' : '0' }}>
                            <PlayButton sourceLocator={pl.source_locator} theme={theme} size="small" />
                            <span style={{ fontSize: '0.56rem', color: theme.textSub }}>{pl.source_locator}</span>
                          </span>
                        )}
                      </div>
                    )}
                        </>
                      );
                    })()}
                  </div>
                ))
              }
            </div>
          </div>
          {/* ── Place edit modal ── */}
          {editingPlace && (() => {
            const ep = editingPlace;
            const fieldStyle = { width: '100%', padding: '6px 8px', fontSize: '0.8rem', borderRadius: '6px',
              border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, boxSizing: 'border-box' };
            const labelStyle = { fontSize: '0.68rem', fontWeight: 600, color: theme.textSub, marginBottom: '2px' };
            const update = (k, v) => setEditingPlace(prev => ({ ...prev, [k]: v }));
            const save = () => {
              const body = {
                place_name: ep.place_name, place_type: ep.place_type || null, canonical: ep.canonical || null,
                country: ep.country || null, lat: ep.lat != null && ep.lat !== '' ? Number(ep.lat) : null,
                lon: ep.lon != null && ep.lon !== '' ? Number(ep.lon) : null,
                year_from: ep.year_from != null && ep.year_from !== '' ? Number(ep.year_from) : null,
                year_to: ep.year_to != null && ep.year_to !== '' ? Number(ep.year_to) : null,
                context: ep.context || null, part_number: ep.part_number != null && ep.part_number !== '' ? Number(ep.part_number) : null,
                source_locator: ep.source_locator || null, verified: !!ep.verified,
              };
              axios.put(`${backendUrl}/subject/places/${ep.id}?root=${encodeURIComponent(root)}`, body, authConfig)
                .then(r => { setPlaces(prev => prev.map(p => p.id === ep.id ? r.data : p)); setEditingPlace(null); })
                .catch(e => toast('place-save', 'error', 'Save failed: ' + (e.response?.data?.detail || e.message)));
            };
            return (
              <div style={{ position: 'fixed', inset: 0, zIndex: Z.modal, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }}
                onClick={() => setEditingPlace(null)}>
                <div onClick={e => e.stopPropagation()} style={{ background: theme.card, border: `1px solid ${theme.border}`,
                  borderRadius: '12px', padding: '20px', width: '420px', maxHeight: '80vh', overflowY: 'auto',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: theme.text }}>Edit Place</span>
                    <button onClick={() => setEditingPlace(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textSub, padding: '2px' }}>
                      <X size={16} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div>
                      <div style={labelStyle}>Canonical Name</div>
                      <input value={ep.canonical || ''} onChange={e => update('canonical', e.target.value)} style={fieldStyle} />
                    </div>
                    <div>
                      <div style={labelStyle}>Spoken Name</div>
                      <input value={ep.place_name || ''} onChange={e => update('place_name', e.target.value)} style={fieldStyle} />
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={labelStyle}>Place Type</div>
                        <select value={ep.place_type || ''} onChange={e => update('place_type', e.target.value)} style={fieldStyle}>
                          <option value="">—</option>
                          {Object.entries(PLACE_TYPE_LABELS).sort((a, b) => a[1].localeCompare(b[1])).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={labelStyle}>Country</div>
                        <input value={ep.country || ''} onChange={e => update('country', e.target.value)} style={fieldStyle} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={labelStyle}>Year From</div>
                        <input type="number" value={ep.year_from ?? ''} onChange={e => update('year_from', e.target.value)} style={fieldStyle} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={labelStyle}>Year To</div>
                        <input type="number" value={ep.year_to ?? ''} onChange={e => update('year_to', e.target.value)} style={fieldStyle} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={labelStyle}>Latitude</div>
                        <input type="number" step="any" value={ep.lat ?? ''} onChange={e => update('lat', e.target.value)} style={fieldStyle} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={labelStyle}>Longitude</div>
                        <input type="number" step="any" value={ep.lon ?? ''} onChange={e => update('lon', e.target.value)} style={fieldStyle} />
                      </div>
                    </div>
                    <div>
                      <div style={labelStyle}>Context</div>
                      <textarea value={ep.context || ''} onChange={e => update('context', e.target.value)}
                        rows={2} style={{ ...fieldStyle, resize: 'vertical' }} />
                    </div>
                    <div>
                      <div style={labelStyle}>Source Locator</div>
                      <input value={ep.source_locator || ''} onChange={e => update('source_locator', e.target.value)} style={fieldStyle} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input type="checkbox" checked={!!ep.verified} onChange={e => update('verified', e.target.checked)} id="place-edit-verified" />
                      <label htmlFor="place-edit-verified" style={{ fontSize: '0.78rem', color: theme.text }}>Verified</label>
                    </div>
                    {/* Modal-editor convention: Delete at the bottom left, red
                        with a Trash2 icon, footer separated by a border. Gated
                        on canDeletePlaces so it matches the card's trash icon
                        and stays admin-only. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px',
                      paddingTop: '12px', borderTop: `1px solid ${theme.border}` }}>
                      {canDeletePlaces && (
                        <Button theme={theme} variant="danger-ghost" size="sm" icon={Trash2}
                          onClick={async () => {
                            if (!await confirm({ message: 'Delete this place card?', title: 'Delete place?', confirmLabel: 'Delete', destructive: true })) return;
                            axios.delete(`${backendUrl}/subject/places/${ep.id}?root=${encodeURIComponent(root)}`, authConfig)
                              .then(() => {
                                setPlaces(prev => prev.filter(p => p.id !== ep.id));
                                setFocusedPlaceId(prev => (prev === ep.id ? null : prev));
                                setEditingPlace(null);
                              })
                              .catch(e => toast('place-delete', 'error', 'Delete failed: ' + (e.response?.data?.detail || e.message)));
                          }}>
                          Delete
                        </Button>
                      )}
                      <div style={{ flex: 1 }} />
                      <Button theme={theme} variant="outline" size="sm" onClick={() => setEditingPlace(null)}>Cancel</Button>
                      <Button theme={theme} variant="primary" size="sm" onClick={save}>Save</Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
          <div style={{ flex: 1, minWidth: 0, minHeight: 0, height: '100%', display: 'flex', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px', overflow: 'hidden' }}>
            <LeafletMap
              places={filteredPlaces}
              isDarkMode={isDarkMode}
              fullscreen={fullscreen}
              focusedPlaceId={focusedPlaceId}
            />
          </div>
        </div>
      )}

      {activeTab === 'family' && (
        <div style={{ flex: '0 0 auto', minHeight: 0, height: fullscreenPaneHeight, maxHeight: fullscreenPaneHeight, display: 'flex', overflow: 'hidden' }}>
          <FamilyTree root={root} theme={theme} backendUrl={backendUrl} subjectRecord={initial} onMembersLoaded={setFamilyCount} currentUser={currentUser} />
        </div>
      )}

      {activeTab === 'timeline' && (
        <div style={{ flex: '0 0 auto', minHeight: 0, height: fullscreenPaneHeight, maxHeight: fullscreenPaneHeight, display: 'flex', overflow: 'hidden' }}>
          <Timeline root={root} theme={theme} backendUrl={backendUrl} videoId={videoId} activeSubdir={activeSubdir} onSeekVideo={onSeekVideo} pushToast={pushToast} logActivity={logActivity} currentUser={currentUser} onOpenMergeReview={onOpenMergeReview} />
        </div>
      )}

      </div>

      {/* ── Sticky footer: always visible ── */}
      {err && <div style={{ color: theme.danger, fontSize: '0.75rem', padding: '6px 8px', background: `${theme.danger}15`, borderRadius: '6px', marginTop: '8px', flexShrink: 0 }}>{err}</div>}

      {/* AI Costs and Save button are now inside the bio tab content above */}

      {/* ── SEED REVIEW MODAL ── */}
      {seedPreview && (
        <div onClick={() => setSeedPreview(null)} style={{ position: 'fixed', inset: 0, zIndex: Z.modal, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '720px', maxHeight: '85vh', overflowY: 'auto', background: theme.card, borderRadius: '16px', padding: '20px', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#a78bfa', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Review Extracted Data
            </div>
            <div style={{ fontSize: '0.68rem', color: theme.textSub, marginBottom: '16px' }}>
              This subject already has data. Review the AI-extracted changes below before applying.
            </div>

            {/* Summary */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {[
                { label: 'Bio fields to fill', count: seedPreview.summary?.subject_diffs || 0, color: '#3b82f6' },
                { label: 'New places', count: seedPreview.summary?.new_places || 0, color: '#22c55e' },
                { label: 'New family', count: seedPreview.summary?.new_family || 0, color: '#f59e0b' },
                { label: 'New timeline', count: seedPreview.summary?.new_timeline || 0, color: '#8b5cf6' },
              ].map(s => (
                <div key={s.label} style={{ padding: '6px 12px', background: `${s.color}15`, border: `1px solid ${s.color}30`, borderRadius: '8px', fontSize: '0.7rem', color: s.color, fontWeight: 700 }}>
                  {s.count} {s.label}
                </div>
              ))}
            </div>

            {/* Subject diffs */}
            {seedPreview.preview?.subject?.diffs?.length > 0 && (
              <details open style={{ marginBottom: '12px' }}>
                <summary style={{ fontSize: '0.72rem', fontWeight: 700, color: '#3b82f6', cursor: 'pointer', marginBottom: '6px' }}>
                  Bio — {seedPreview.preview.subject.diffs.length} empty fields to fill
                </summary>
                <div style={{ display: 'grid', gap: '4px', paddingLeft: '8px' }}>
                  {seedPreview.preview.subject.diffs.map(d => (
                    <div key={d.field} style={{ display: 'flex', gap: '8px', fontSize: '0.65rem', padding: '4px 8px', background: isDarkMode ? 'rgba(59,130,246,0.06)' : 'rgba(59,130,246,0.04)', borderRadius: '6px' }}>
                      <span style={{ fontWeight: 700, color: theme.textSub, minWidth: '120px', textTransform: 'capitalize' }}>{d.field.replace(/_/g, ' ')}</span>
                      <span style={{ color: '#22c55e' }}>{d.proposed}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* New places */}
            {seedPreview.preview?.places?.new_only?.length > 0 && (
              <details open style={{ marginBottom: '12px' }}>
                <summary style={{ fontSize: '0.72rem', fontWeight: 700, color: '#22c55e', cursor: 'pointer', marginBottom: '6px' }}>
                  Places — {seedPreview.preview.places.new_only.length} new
                </summary>
                <div style={{ display: 'grid', gap: '3px', paddingLeft: '8px' }}>
                  {seedPreview.preview.places.new_only.map((p, i) => (
                    <div key={i} style={{ fontSize: '0.65rem', padding: '3px 8px', background: isDarkMode ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.04)', borderRadius: '6px', color: theme.text }}>
                      <strong>{p.place_name}</strong> {p.place_type && <span style={{ color: theme.textSub }}>({p.place_type})</span>} {p.country && <span style={{ color: theme.textSub }}>— {p.country}</span>}
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* New family */}
            {seedPreview.preview?.family?.new_only?.length > 0 && (
              <details open style={{ marginBottom: '12px' }}>
                <summary style={{ fontSize: '0.72rem', fontWeight: 700, color: '#f59e0b', cursor: 'pointer', marginBottom: '6px' }}>
                  Family — {seedPreview.preview.family.new_only.length} new
                </summary>
                <div style={{ display: 'grid', gap: '3px', paddingLeft: '8px' }}>
                  {seedPreview.preview.family.new_only.map((f, i) => (
                    <div key={i} style={{ fontSize: '0.65rem', padding: '3px 8px', background: isDarkMode ? 'rgba(245,158,11,0.06)' : 'rgba(245,158,11,0.04)', borderRadius: '6px', color: theme.text }}>
                      <strong>{f.name}</strong> <span style={{ color: theme.textSub }}>({f.relation})</span>
                      {f.birth_year && <span style={{ color: theme.textSub }}> b.{f.birth_year}</span>}
                      {f.death_year && <span style={{ color: theme.textSub }}> d.{f.death_year}</span>}
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* New timeline */}
            {seedPreview.preview?.timeline?.new_only?.length > 0 && (
              <details open style={{ marginBottom: '12px' }}>
                <summary style={{ fontSize: '0.72rem', fontWeight: 700, color: '#8b5cf6', cursor: 'pointer', marginBottom: '6px' }}>
                  Timeline — {seedPreview.preview.timeline.new_only.length} new
                </summary>
                <div style={{ display: 'grid', gap: '3px', paddingLeft: '8px' }}>
                  {seedPreview.preview.timeline.new_only.map((e, i) => (
                    <div key={i} style={{ fontSize: '0.65rem', padding: '3px 8px', background: isDarkMode ? 'rgba(139,92,246,0.06)' : 'rgba(139,92,246,0.04)', borderRadius: '6px', color: theme.text }}>
                      <strong>{e.year}</strong> — {e.title} {e.category && e.category !== 'other' && <span style={{ color: theme.textSub }}>[{e.category}]</span>}
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* No changes */}
            {(seedPreview.summary?.total_new || 0) === 0 && (seedPreview.summary?.subject_diffs || 0) === 0 && (
              <div style={{ padding: '16px', textAlign: 'center', color: theme.textSub, fontSize: '0.75rem' }}>
                No new data found — everything is already up to date.
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', borderTop: `1px solid ${theme.border}`, paddingTop: '14px' }}>
              <button onClick={() => applySeedPreview({ accept_subject: true, accept_places: true, accept_family: true, accept_timeline: true })}
                disabled={seedApplying || ((seedPreview.summary?.total_new || 0) === 0 && (seedPreview.summary?.subject_diffs || 0) === 0)}
                style={{ flex: 1, padding: '10px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', opacity: seedApplying ? 0.6 : 1 }}>
                {seedApplying ? 'Applying…' : 'Accept All & Apply'}
              </button>
              {(seedPreview.summary?.total_new || 0) > 0 && (
                <button onClick={() => applySeedPreview({ accept_subject: true, accept_places: false, accept_family: false, accept_timeline: false })}
                  disabled={seedApplying || (seedPreview.summary?.subject_diffs || 0) === 0}
                  style={{ padding: '10px 14px', background: 'none', color: '#3b82f6', border: `1px solid #3b82f6`, borderRadius: '8px', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer' }}>
                  Bio Only
                </button>
              )}
              <Button theme={theme} variant="outline" size="md" onClick={() => setSeedPreview(null)}>
                Discard
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default SubjectEditForm;
